import React, { useEffect, useState, useRef } from "react";
import SellerSidebar from "../../components/SellerSidebar";
import Card from "../../components/Card";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ShareModal from "../../components/ShareModal";
import CategorySelectDropdown from "../../components/CategorySelectDropdown";
import { 
  getSellerProfile, 
  subscribeReminder, 
  favouriteUnfavouriteStream,
  getProductCategories,
  getProductSubCategories
} from "../../services/stream.service";
import { capitalizeFirstLetter, formatDateAndTime, getUser, isGuest, isUserLoggedIn, showLoginRequiredModal } from "../../../../utils/commonfunction";
import { toggleFavouriteSeller } from "../../services/user.service";
import { favouriteUnfavouriteProduct } from "../../services/product.services";
import ShoplistSelectModal from "../../components/ShoplistSelectModal";
import { ROLE } from "../../../../utils/Constants";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { Share2, Copy, ChevronLeft, ChevronRight, MapPin, Store, RotateCcw } from "lucide-react";

import AvatarImage from "../../components/AvatarImage";
import { formatPhoneForWhatsapp, isMobileDevice } from "../../services/message.service";

const getPaginationPages = (currentPage, totalPages) => {
  if (totalPages <= 4) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 2) {
    return [1, 2, "...", totalPages];
  }
  if (currentPage >= totalPages - 1) {
    return [1, "...", totalPages - 1, totalPages];
  }
  return [1, "...", currentPage, "...", totalPages];
};

function SellerDetails() {
  const { t } = useTranslation();
  const { handle } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sellerId = handle || searchParams.get("o_id");

  const urlCategory = searchParams.get("categoryId") || searchParams.get("category") || "";
  const urlSubCategory = searchParams.get("subcategoryId") || searchParams.get("subCategory") || "";
  const urlTab = searchParams.get("tab") || (urlCategory || urlSubCategory ? "products" : "live");

  const [activeTab, setActiveTab] = useState(urlTab);
  const userData = getUser(ROLE.USER) || {};
  const [seller, setSeller] = useState(null);
  const [liveStreams, setLiveStreams] = useState([]);
  const [upcomingStreams, setUpcomingStreams] = useState([]);
  const [paststreams, setPaststreams] = useState([]);
  const [productPage, setProductPage] = useState(1);
  const [totalProductPages, setTotalProductPages] = useState(1);
  const [productLimit] = useState(20);
  const [modalProduct, setModalProduct] = useState(null);
  const [isShoplistModalOpen, setIsShoplistModalOpen] = useState(false);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isFavouriteSeller, setIsFavouriteSeller] = useState(false);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);

  // Filters State
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [allSellerSubCategories, setAllSellerSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(urlCategory);
  const [selectedSubCategory, setSelectedSubCategory] = useState(urlSubCategory);

  const [showShareModal, setShowShareModal] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const isInitialMount = useRef(true);

  const [failedBanners, setFailedBanners] = useState({});

  const rawBannerImages = (seller?.store_images && seller.store_images.length > 0)
    ? seller.store_images
    : (seller?.store_image ? [seller.store_image] : []);

  const bannerImages = rawBannerImages.filter((img) => img && !failedBanners[img]);

  const cleanSellerPhone = formatPhoneForWhatsapp(
    seller?.kyc_mobile_number || seller?.kyc_number || seller?.phone_number
  );
  const isMobile = typeof window !== "undefined" && isMobileDevice();
  const sellerWhatsappUrl = cleanSellerPhone
    ? isMobile
      ? `https://api.whatsapp.com/send?phone=${cleanSellerPhone}`
      : `https://web.whatsapp.com/send?phone=${cleanSellerPhone}`
    : null;

  useEffect(() => {
    // Fetch categories for this seller
    if (sellerId) {
      getProductCategories({ seller_id: sellerId })
        .then(resp => {
          if (resp?.success && resp.data?.list) {
            setCategories(resp.data.list);
          }
        })
        .catch(console.error);

      // If URL has category, fetch subcategories
      if (urlCategory) {
        getProductSubCategories(urlCategory, { seller_id: sellerId })
          .then(resp => {
            if (resp?.success && resp.data?.list) {
              setSubCategories(resp.data.list);
            }
          })
          .catch(console.error);
      }
    }

    // Release initial mount flag after a delay to avoid wiping initial params in cascading effects
    const mountTimer = setTimeout(() => {
      isInitialMount.current = false;
    }, 250);

    return () => clearTimeout(mountTimer);
  }, [sellerId]);

  // Cascading subcategories on user change
  useEffect(() => {
    if (isInitialMount.current) return;
    setSelectedSubCategory("");
    if (selectedCategory) {
      if (allSellerSubCategories && allSellerSubCategories.length > 0) {
        setSubCategories(
          allSellerSubCategories.filter(
            (sub) => String(sub.categoryId) === String(selectedCategory)
          )
        );
      } else {
        getProductSubCategories(selectedCategory, { seller_id: seller?._id || sellerId })
          .then(resp => {
            if (resp?.success && resp.data?.list) {
              setSubCategories(resp.data.list);
            }
          })
          .catch(console.error);
      }
    } else {
      setSubCategories([]);
    }
  }, [selectedCategory, allSellerSubCategories, seller?._id, sellerId]);

  // Reset page when filters change
  useEffect(() => {
    setProductPage(1);
  }, [selectedCategory, selectedSubCategory]);

  const clearAllFilters = () => {
    setSelectedCategory("");
    setSelectedSubCategory("");
    setProductPage(1);
  };

  const hasActiveFilters = Boolean(
    selectedCategory ||
    selectedSubCategory
  );

  // Sync state to URL searchParams
  useEffect(() => {
    if (isInitialMount.current) return;

    const currentCategory = searchParams.get("categoryId") || searchParams.get("category") || "";
    const currentSub = searchParams.get("subcategoryId") || searchParams.get("subCategory") || "";
    const currentTab = searchParams.get("tab") || "";

    const categoryChanged = currentCategory !== selectedCategory;
    const subChanged = currentSub !== selectedSubCategory;
    const expectedTab = (activeTab && (activeTab !== "live" || hasActiveFilters)) ? activeTab : "";
    const tabChanged = currentTab !== expectedTab;

    if (categoryChanged || subChanged || tabChanged) {
      const nextParams = new URLSearchParams();
      if (!handle && sellerId) nextParams.set("o_id", sellerId);

      // Tab
      if (expectedTab) {
        nextParams.set("tab", expectedTab);
      }

      // Category filters
      if (selectedCategory) nextParams.set("categoryId", selectedCategory);
      if (selectedSubCategory) nextParams.set("subcategoryId", selectedSubCategory);

      setSearchParams(nextParams, { replace: true });
    }
  }, [selectedCategory, selectedSubCategory, activeTab, sellerId, handle, hasActiveFilters]);

  // Sync URL searchParams back to state (for browser back/forward or external navigation)
  useEffect(() => {
    if (isInitialMount.current) return;

    const urlCat = searchParams.get("categoryId") || searchParams.get("category") || "";
    const urlSub = searchParams.get("subcategoryId") || searchParams.get("subCategory") || "";
    const urlTabParam = searchParams.get("tab") || "";

    if (urlCat !== selectedCategory) {
      setSelectedCategory(urlCat);
      if (urlCat) {
        if (allSellerSubCategories && allSellerSubCategories.length > 0) {
          setSubCategories(
            allSellerSubCategories.filter(
              (sub) => String(sub.categoryId) === String(urlCat)
            )
          );
        } else {
          getProductSubCategories(urlCat, { seller_id: seller?._id || sellerId }).then(resp => {
            if (resp?.success && resp.data?.list) setSubCategories(resp.data.list);
          }).catch(console.error);
        }
      } else {
        setSubCategories([]);
      }
    }

    if (urlSub !== selectedSubCategory) {
      setSelectedSubCategory(urlSub);
    }

    if (urlTabParam && urlTabParam !== activeTab) {
      setActiveTab(urlTabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (bannerImages.length <= 1) {
      setCurrentBannerIndex(0);
      return;
    }
    const timer = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % bannerImages.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [bannerImages.length]);

  useEffect(() => {
    if (!sellerId) return;

    if (!seller) {
      setLoading(true);
    } else {
      setProductsLoading(true);
    }

    const payload = {
      product_page: productPage,
      product_limit: productLimit,
      categoryId: selectedCategory || undefined,
      subcategoryId: selectedSubCategory || undefined,
    };

    getSellerProfile(sellerId, payload)
      .then((res) => {
        if (res.data) {
          const data = res.data;

          setSeller(data.seller || null);
          setLiveStreams(data.liveStreams || []);
          setUpcomingStreams(data.upcomingStreams || []);
          setPaststreams(data.pastStreams || []);
          setProducts(data.products || []);

          if (Array.isArray(data.categories)) {
            setCategories(data.categories);
          }
          if (Array.isArray(data.subcategories)) {
            setAllSellerSubCategories(data.subcategories);
            const activeCat = selectedCategory || urlCategory;
            if (activeCat) {
              setSubCategories(
                data.subcategories.filter(
                  (sub) => String(sub.categoryId) === String(activeCat)
                )
              );
            } else {
              setSubCategories([]);
            }
          }

          if (userData?.isAdvertiser === false) {
            setActiveTab("products");
          }

          setTotalProductPages(
            data.productPagination?.totalPages || 1
          );

          // URL normalization: if accessed via legacy o_id route, old /advertiser/ route, or unnormalized handle, update route to /businessprofilename/handle or /businessprofilename/store-name
          const sellerObj = data.seller;
          const storeSlug = (sellerObj?.store_name?.trim() || sellerObj?.name?.trim() || "").replace(/\s+/g, "-");
          const rawHandle = sellerObj?.business_handle?.trim();
          const cleanHandle = rawHandle ? rawHandle.replace(/^@+/, "") : "";
          const canonicalHandle = cleanHandle || storeSlug;

          const currentPath = window.location.pathname;
          const currentHandle = handle ? decodeURIComponent(handle).replace(/^@+/, "") : "";
          const isLegacyRoute = currentPath.startsWith("/advertiser/") || currentPath.startsWith("/live-streams/seller-details") || currentPath.startsWith("/business-profile-name/");

          if (canonicalHandle && (currentHandle !== canonicalHandle || isLegacyRoute || currentPath.includes("%40") || (handle && handle.startsWith("@")))) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("o_id");
            const q = nextParams.toString() ? `?${nextParams.toString()}` : "";
            navigate(`/businessprofilename/${encodeURIComponent(canonicalHandle)}${q}`, { replace: true });
          }
        }
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
        setProductsLoading(false);
      });

  }, [sellerId, productPage, selectedCategory, selectedSubCategory]);

  const handleRemindMe = async (item) => {
    const user = getUser(ROLE.USER);
    if (!user || !user.token) {
      Swal.fire({
        icon: "error",
        text: t("common.login_required"),
      });
      return;
    }
    try {
      const resp = await subscribeReminder(item._id);
      if (resp.success) {
        Swal.fire({
          icon: 'success',
          text: t("streams.reminder_set_msg", { title: item.title }),
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
        setUpcomingStreams(prev =>
          prev.map(s => s._id === item._id ? { ...s, isReminderSet: true } : s)
        );
      }
    } catch (error) {
      console.error("Failed to set reminder:", error);
    }
  };


  const handleLikeFavourite = async (item) => {
    const user = getUser(ROLE.USER);
    if (!user || !user.token) {
      Swal.fire({
        icon: "error",
        text: t("common.login_required"),
      });
      return;
    }
    try {
      const resp = await favouriteUnfavouriteStream(item._id);
      if (resp.success) {
        Swal.fire({
          icon: 'success',
          text: resp.message,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });

        const updateArray = (arr) => arr?.map(s => s._id === item._id ? { ...s, isFavouriteStream: !s.isFavouriteStream } : s);
        setLiveStreams(prev => updateArray(prev));
        setUpcomingStreams(prev => updateArray(prev));
      }
    } catch (error) {
      console.error("Failed to toggle favourite:", error);
    }
  };


  const handleToggleFavouriteSeller = async () => {
    const user = getUser(ROLE.USER);
    if (!user || !user.token) {
      Swal.fire({
        icon: "error",
        text: t("common.login_required"),
      });
      return;
    }
    try {
      const resp = await toggleFavouriteSeller(seller?._id || sellerId);
      if (resp.success) {
        Swal.fire({
          icon: 'success',
          text: resp.message,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
        setSeller(prev => ({ ...prev, isFavouriteSeller: !prev.isFavouriteSeller }));
      }
    } catch (error) {
      console.error("Failed to toggle seller favourite:", error);
    }
  };

  const handleLikeFavouriteProduct = async (item) => {
    const user = getUser(ROLE.USER);
    const isLoggedIn = Boolean(
      user && 
      (user._id || user.id || user.token) && 
      !isGuest()
    );

    if (!isLoggedIn) {
      Swal.fire({
        icon: "warning",
        title: t("common.login_required"),
        text: t("common.login_required_to_favorite", { defaultValue: "Please login to add to favourites" }),
        showCancelButton: true,
        confirmButtonColor: "var(--primary-400, #ff4081)",
        cancelButtonColor: "#71717a",
        confirmButtonText: t("link_login", { defaultValue: "Login" }),
        cancelButtonText: t("product_details.close", { defaultValue: "Close" }),
      }).then((result) => {
        if (result.isConfirmed) {
          const currentPath = window.location.pathname + window.location.search;
          navigate(`/user/login?redirect=${encodeURIComponent(currentPath)}`);
        }
      });
      return;
    }

    setModalProduct(item);
    setIsShoplistModalOpen(true);
  };

  const handleShoplistSaved = (savedShoplistIds) => {
    if (modalProduct) {
      const isFav = Array.isArray(savedShoplistIds) && savedShoplistIds.length > 0;
      setProducts((prev) =>
        prev.map((p) =>
          p._id === modalProduct._id
            ? { ...p, isFavourite: isFav, isFavouriteProduct: isFav }
            : p
        )
      );
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      Swal.fire({
        icon: "success",
        text: "Store link with filters copied to clipboard!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShareProfile = () => {
    setShowShareModal(true);
  };
  

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="seller-details py-10">
      <div className="container">
        {/* Main Grid Layout (Sidebar and Main Content) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <SellerSidebar data={seller} />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Store Information Title */}
            <div className="flex items-center justify-between gap-[10px] flex-wrap">
              <h2 className="headings-h2 text-zinc-950">{t("seller_details.store_information")}</h2>
              <div className="flex items-center gap-2.5">
                {sellerWhatsappUrl && (
                  <a
                    href={sellerWhatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#25D366] bg-[#25D366]/10 hover:bg-[#25D366] text-[#128C7E] hover:text-white text-xs font-semibold shadow-xs transition-all active:scale-95 cursor-pointer no-underline group"
                    title="WhatsApp Us"
                  >
                    <img src="/images/whatsapp.svg" alt="WhatsApp" className="w-3.5 h-3.5 shrink-0" />
                    <span>{t("seller_details.whatsapp_us", "WhatsApp Us")}</span>
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gray-200 bg-white hover:border-[var(--secondary-500)] text-zinc-700 hover:text-[var(--secondary-500)] text-xs font-semibold shadow-xs transition-all active:scale-95 cursor-pointer"
                  title="Share Store"
                >
                  <Share2 className="w-3.5 h-3.5 text-[var(--secondary-500)]" />
                  <span>Share Store</span>
                </button>
              </div>
            </div>

            {/* Big Store Banner Hero Card */}
            <div className="relative w-full rounded-[20px] overflow-hidden shadow-xl border border-zinc-200 bg-zinc-900 group">
              {/* Banner Image / Background Slider */}
              <div className="relative w-full h-[260px] sm:h-[320px] md:h-[360px] overflow-hidden bg-gradient-to-r from-slate-950 via-purple-950 to-slate-900">
                {bannerImages.length > 0 ? (
                  <img
                    src={bannerImages[currentBannerIndex]}
                    alt={`${seller?.store_name || "Store"} banner`}
                    className="w-full h-full object-cover transition-all duration-700 ease-in-out"
                    onError={() => {
                      const failedUrl = bannerImages[currentBannerIndex];
                      if (failedUrl) {
                        setFailedBanners((prev) => ({ ...prev, [failedUrl]: true }));
                      }
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-r from-slate-950 via-purple-950 to-slate-900 flex items-center justify-center">
                    <Store className="w-20 h-20 text-white/20" />
                  </div>
                )}

                {/* Dark Gradient Overlay for readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />

                {/* Top Right Quick Actions */}
                <div className="absolute top-4 right-4 flex items-center gap-2.5 z-20">
                  {bannerImages.length > 1 && (
                    <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-white/90 border border-white/10 shadow-sm">
                      {currentBannerIndex + 1} / {bannerImages.length}
                    </span>
                  )}
                  {sellerWhatsappUrl && (
                    <a
                      href={sellerWhatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-[42px] h-[42px] rounded-full bg-black/40 hover:bg-[#25D366] backdrop-blur-md flex items-center justify-center text-white transition-all shadow-md cursor-pointer border border-white/20 group"
                      title="WhatsApp Us"
                    >
                      <img src="/images/whatsapp.svg" alt="WhatsApp" className="w-4 h-4 brightness-0 invert" />
                    </a>
                  )}
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="w-[42px] h-[42px] rounded-full bg-black/40 hover:bg-black/70 backdrop-blur-md flex items-center justify-center text-white transition-all shadow-md cursor-pointer border border-white/20"
                    title="Share Store"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="w-[42px] h-[42px] rounded-full bg-black/40 hover:bg-black/70 backdrop-blur-md flex items-center justify-center text-white transition-all shadow-md cursor-pointer border border-white/20"
                    title="Copy Link"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleToggleFavouriteSeller}
                    className={`w-[42px] h-[42px] rounded-full backdrop-blur-md flex items-center justify-center transition-all shadow-md cursor-pointer border border-white/20 ${
                      seller?.isFavouriteSeller
                        ? "bg-[#FF63B5] text-white border-transparent"
                        : "bg-black/40 hover:bg-black/70 text-white"
                    }`}
                    title="Favourite Seller"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                      fill={seller?.isFavouriteSeller ? "currentColor" : "none"}
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                      />
                    </svg>
                  </button>
                </div>

                {/* Banner Carousel Arrows */}
                {bannerImages.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setCurrentBannerIndex((prev) =>
                          prev === 0 ? bannerImages.length - 1 : prev - 1
                        )
                      }
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 backdrop-blur-md text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-20 shadow-md cursor-pointer border border-white/10"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentBannerIndex((prev) => (prev + 1) % bannerImages.length)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 backdrop-blur-md text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-20 shadow-md cursor-pointer border border-white/10"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Bottom Store Details Bar inside Banner */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 z-20">
                  <div className="flex items-center gap-4">
                    <AvatarImage
                      src={seller?.store_image || seller?.profile_image}
                      name={seller?.store_name || seller?.name}
                      alt="Store Logo"
                      className="w-[72px] h-[72px] sm:w-[88px] sm:h-[88px] object-cover rounded-full ring-4 ring-white/90 shadow-2xl shrink-0 border-2 border-pink-400 bg-white"
                      style={{ fontSize: "28px" }}
                    />
                    <div className="text-white drop-shadow-md">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                        {capitalizeFirstLetter(seller?.store_name || seller?.name || "")}
                      </h2>
                      {seller?.store_location?.store_address && (
                        <p className="text-xs sm:text-sm text-zinc-200/90 flex items-center gap-1.5 mt-1 font-medium">
                          <MapPin className="w-4 h-4 text-pink-400 shrink-0" />
                          <span>{seller.store_location.store_address}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Banner Indicators (Dots) */}
                  {bannerImages.length > 1 && (
                    <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10 self-center sm:self-end">
                      {bannerImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentBannerIndex(idx)}
                          className={`h-2 rounded-full transition-all duration-300 ${
                            idx === currentBannerIndex
                              ? "w-6 bg-[#FF63B5]"
                              : "w-2 bg-white/50 hover:bg-white/80"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="tabs tabs-boxed bg-transparent border-b border-gray-200 flex items-center gap-[15px] overflow-x-auto whitespace-nowrap">
              {userData?.isAdvertiser !== false && (
                <>
                  <button
                    className={`tab tab-lg px-[15px] font-semibold rounded-none ${activeTab === "live"
                      ? "text-[#FF63B5] border-b-2 border-[#FF63B5]"
                      : "text-gray-500 border-b-2 border-transparent"
                      }`}
                    onClick={() => setActiveTab("live")}
                  >
                    {t("header.live_stream")}
                  </button>
                  <button
                    className={`tab tab-lg px-[15px] font-semibold rounded-none ${activeTab === "upcoming"
                      ? "text-[#FF63B5] border-b-2 border-[#FF63B5]"
                      : "text-gray-500 border-b-2 border-transparent"
                      }`}
                    onClick={() => setActiveTab("upcoming")}
                  >
                    {t("header.upcoming_stream")}
                  </button>
                  <button
                    className={`tab tab-lg px-[15px] font-semibold rounded-none ${activeTab === "past"
                      ? "text-[#FF63B5] border-b-2 border-[#FF63B5]"
                      : "text-gray-500 border-b-2 border-transparent"
                      }`}
                    onClick={() => setActiveTab("past")}
                  >
                    {t("header.past_streams")}
                  </button>
                </>
              )}
              <button
                className={`tab tab-lg px-[15px] font-semibold rounded-none ${activeTab === "products"
                  ? "text-[#FF63B5] border-b-2 border-[#FF63B5]"
                  : "text-gray-500 border-b-2 border-transparent"
                  }`}
                onClick={() => setActiveTab("products")}
              >
                {t("header.products")}
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content-bg">
              {activeTab === "live" && (
                <div className="tab-pane active">
                  <div className="mb-[30px]">
                    <h2 className="headings-h2 text-zinc-950 mb-4">
                      {t("header.live_stream")}
                    </h2>
                    {liveStreams && liveStreams.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {liveStreams?.map((item, index) => (
                          <div key={index} className="px-[15px] cursor-pointer" onClick={() => {
                            const targetUrl = `/user/streams/stream-details/${item?._id}`;
                            if (!isUserLoggedIn()) {
                              showLoginRequiredModal({
                                navigate,
                                redirectUrl: targetUrl,
                                text: t("common.login_required_to_view_stream", { defaultValue: "Please login to view stream details" }),
                              });
                              return;
                            }
                            navigate(targetUrl);
                          }}>
                            <Card
                              key={index}
                              images={item?.thumbnail}
                              title={item?.title}
                              date={formatDateAndTime(item.date)}
                              subtitle={item?.description}
                              time={item?.duration}
                              live={true}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-zinc-500 text-lg font-medium">
                          {t("streams.no_live")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "upcoming" && (
                <div className="tab-pane active">
                  <div className="mb-[30px]">
                    <h2 className="headings-h2 text-zinc-950 mb-4">
                      {t("header.upcoming_stream")}
                    </h2>
                    {upcomingStreams && upcomingStreams.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {upcomingStreams?.map((item, index) => (
                          <Card
                            key={index}
                            images={item?.thumbnail}
                            title={item?.title}
                            date={formatDateAndTime(item.date)}
                            subtitle={item?.description}
                            time={item?.duration}
                            buttonReminder={item.isReminderSet ? t("streams.reminder_set_btn") : t("streams.remind_me_btn")}
                            onReminderClick={() => {
                              if (!item.isReminderSet) handleRemindMe(item);
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-zinc-500 text-lg font-medium">
                          {t("streams.no_upcoming")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "past" && (
                <div className="tab-pane active">
                  <div className="mb-[30px]">
                    <h2 className="headings-h2 text-zinc-950 mb-4">
                      {t("header.past_streams")}
                    </h2>
                    {paststreams && paststreams.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {paststreams?.map((item, index) => (
                          <div key={index} className="px-[15px] cursor-pointer" onClick={() => {
                            const targetUrl = `/user/streams/stream-details/${item?._id}`;
                            if (!isUserLoggedIn()) {
                              showLoginRequiredModal({
                                navigate,
                                redirectUrl: targetUrl,
                                text: t("common.login_required_to_view_stream", { defaultValue: "Please login to view stream details" }),
                              });
                              return;
                            }
                            navigate(targetUrl);
                          }}>
                            <Card
                              key={index}
                              images={item?.thumbnail}
                              title={item?.title}
                              date={formatDateAndTime(item.date)}
                              subtitle={item?.subtitle}
                              time={item?.time}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-zinc-500 text-lg font-medium">
                          {t("streams.no_past")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "products" && (
                <div className="tab-pane">
                  {/* Categories/Subcategories Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 items-center justify-center gap-3 mb-8 animate-fadeIn">
                    <CategorySelectDropdown
                      value={selectedCategory}
                      onChange={(val) => setSelectedCategory(val)}
                      options={categories}
                      placeholder={t("search.all_categories")}
                      fallbackIcon="/category.png"
                    />

                    <CategorySelectDropdown
                      value={selectedSubCategory}
                      onChange={(val) => setSelectedSubCategory(val)}
                      options={subCategories}
                      placeholder={t("search.all_subcategories")}
                      disabled={!selectedCategory}
                      fallbackIcon="/category.png"
                    />
                  </div>

                  <div className="mb-[0px]">
                    <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                      <h2 className="headings-h2 text-zinc-950">
                        {t("header.products")}
                      </h2>
                      <div className="flex items-center gap-2">
                        {hasActiveFilters && (
                          <button
                            type="button"
                            onClick={clearAllFilters}
                            className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-xl transition-all cursor-pointer"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Reset Filters
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowShareModal(true)}
                          className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-[var(--secondary-500)] bg-white hover:bg-gray-50 border border-gray-200 hover:border-[var(--secondary-500)] px-3 py-1.5 rounded-xl transition-all shadow-2xs cursor-pointer"
                          title="Share Products with Filters"
                        >
                          <Share2 className="w-3.5 h-3.5 text-[var(--secondary-500)]" />
                          <span>Share</span>
                        </button>
                      </div>
                    </div>
                    {productsLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="w-10 h-10 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : products && products.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-3 sm:gap-4">
                        {products?.map((item, index) => (
                          <div
                            key={item?._id || index}
                            className="cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                            onClick={() => {
                              const targetUrl = `/user/product-details?o_id=${item?._id}`;
                              if (!isUserLoggedIn()) {
                                showLoginRequiredModal({
                                  navigate,
                                  redirectUrl: targetUrl,
                                });
                                return;
                              }
                              navigate(targetUrl);
                            }}
                          >
                            <Card
                              key={index}
                              images={item?.compressed_image || item?.image}
                              compressedImage={item?.compressed_image}
                              originalImage={item?.image || item?.original_image}
                              title={item?.title}
                              subtitle={item?.description}
                              price={item?.price}
                              isAskForPrice={item?.isAskForPrice}
                              time={item?.duration}
                              live={false}
                              isLiked={Boolean(item?.isFavourite || item?.isFavouriteProduct)}
                              onLikeClick={(e) => {
                                e.stopPropagation();
                                handleLikeFavouriteProduct(item);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-zinc-500 text-lg font-medium">
                          {t("products.no_products")}
                        </p>
                      </div>
                    )}
                  </div>
                  {totalProductPages > 1 && (
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 mt-10 pb-6 flex-wrap">
                      {/* Previous */}
                      <button
                        className="px-3.5 py-1.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs"
                        disabled={productPage <= 1}
                        onClick={() => {
                          setProductPage((prev) => Math.max(1, prev - 1));
                          window.scrollTo({ top: 400, behavior: "smooth" });
                        }}
                      >
                        {t("common.prev")}
                      </button>

                      {/* Page Numbers with spread operation / ellipsis */}
                      {getPaginationPages(productPage, totalProductPages).map((p, idx) =>
                        p === "..." ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="px-2 text-sm font-bold text-gray-400 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={`page-${p}`}
                            onClick={() => {
                              setProductPage(p);
                              window.scrollTo({ top: 400, behavior: "smooth" });
                            }}
                            className={`min-w-[36px] h-9 px-3 py-1 text-sm font-semibold rounded-xl border transition shadow-2xs ${
                              productPage === p
                                ? "bg-[#FF63B5] text-white border-[#FF63B5] shadow-xs"
                                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )}

                      {/* Next */}
                      <button
                        className="px-3.5 py-1.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs"
                        disabled={productPage >= totalProductPages}
                        onClick={() => {
                          setProductPage((prev) => Math.min(totalProductPages, prev + 1));
                          window.scrollTo({ top: 400, behavior: "smooth" });
                        }}
                      >
                        {t("common.next")}
                      </button>
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        seller={seller}
        type="seller"
        shareUrl={window.location.href}
      />

      {modalProduct && (
        <ShoplistSelectModal
          isOpen={isShoplistModalOpen}
          product={modalProduct}
          onClose={() => {
            setIsShoplistModalOpen(false);
            setModalProduct(null);
          }}
          onSaved={handleShoplistSaved}
        />
      )}
    </div>
  );
}

export default SellerDetails;
